/**
 * `SimDriver` — the one implementation of "apply the authoritative stream to
 * an engine state", used by the server and by both of a client's boards.
 *
 * This is the piece that makes the netcode contract mechanically true rather
 * than aspirationally true. The server replays a player's inputs and compares
 * hashes; that comparison only means something if the *rules for when things
 * happen* are identical on both ends. Those rules are small but unforgiving,
 * and one tick runs them in this order:
 *
 * 1. A garbage batch enters `pendingGarbage` at the start of the tick the
 *    server named, **before** that tick's inputs. Not on arrival — arrival
 *    time is a network artefact and would put the two sides' queues out of
 *    step, and the queue is inside the hash.
 * 2. The `TickRule` — the rising floor — runs after the due garbage and still
 *    before that tick's inputs. It is a pure function of the tick number
 *    (`tick-rule.ts`), so a driver rebuilt from a snapshot mid-round picks the
 *    cadence up exactly where the other end has it.
 * 3. The tick's gravity modifier is applied: the board's own ease (a seat's
 *    handicap, fixed for the round) times RUSH's factor while a RUSH window is
 *    open. Whole units in the accumulator `hashState` writes, through the
 *    engine's `applyGravityModifier`.
 * 4. The tick's inputs are delivered, on the tick their `frame` names. While a
 *    MIRROR window is open, `left` and `right` are swapped on the events
 *    handed to the engine's own `step`, and *only* there: what is relayed to
 *    every other simulation of this board is the event exactly as it was
 *    received, so a mirror inverts it again for itself rather than receiving
 *    an already-inverted action it would invert a second time.
 * 5. The engine steps.
 *
 * Curse effects are windows, not counters — `curseActiveAt` asks "is this tick
 * inside one", which is why a driver rebuilt mid-effect from a snapshot picks
 * the remaining half of it up correctly. See `curses.ts`.
 *
 * Nothing else touches the state. Writing those rules twice — once in
 * `apps/server`, once in `apps/client` — would be writing the desync in by
 * hand, so they are written once, here, under the same purity fence as the
 * engine. `docs/decisions/0013` and `0050`.
 */
import {
  GRAVITY_PERMILLE,
  applyGravityModifier,
  cloneGameState,
  hashState,
  queueGarbage,
  step,
} from '@interlock/engine';
import type { GameState, InputEvent, LockResult, StepResult } from '@interlock/engine';

import { curseActiveAt, gravityModifierAt, invertLeftRight, isCurseWindow } from './curses.js';
import type { CurseWindow } from './curses.js';
import { isGarbageSchedule } from './schedule.js';
import type { GarbageSchedule } from './schedule.js';
import { applyTickRule } from './tick-rule.js';
import type { TickRule } from './tick-rule.js';

/**
 * A board hash at a piece-count checkpoint.
 *
 * Produced by the driver rather than by its caller, so that both ends take the
 * hash at the same instant — the end of the tick on which `piecesPlaced`
 * crossed a multiple of N, garbage release included. A caller reaching for
 * `hashState` itself, a tick later or a tick earlier, is the mistake this type
 * exists to make impossible.
 */
export interface HashCheckpoint {
  readonly piecesPlaced: number;
  readonly hash: number;
}

/** What one call to `advanceTo` did, accumulated across the ticks it ran. */
export interface DriveResult {
  readonly ticksAdvanced: number;
  readonly locks: readonly LockResult[];
  readonly garbageApplied: number;
  readonly toppedOut: boolean;
}

const NO_INPUTS: readonly InputEvent[] = [];
const NO_CHECKPOINTS: readonly HashCheckpoint[] = [];

export interface SimDriverOptions {
  /** Piece cadence for hash checkpoints. Both ends pass the same constant. */
  readonly hashEveryNPieces: number;
  /**
   * The rule applied at the start of every tick — the rising floor, for the
   * games that have one. Both ends build it from the same room rules, so it
   * is the same rule. Omitted, there is none.
   */
  readonly tickRule?: TickRule;
  /**
   * This board's gravity, in thousandths of the game's own speed, for the
   * whole life of this driver. Omitted or `1000` changes nothing. This is the
   * handicap's gravity ease — decided once, at round start, from past results
   * (`docs/decisions/0033`): `920` is gravity eased by 8%. It multiplies with
   * a live RUSH window rather than being replaced by one.
   *
   * A positive integer. A driver refuses anything else, because the number
   * goes into hashed state every tick.
   */
  readonly gravityPermille?: number;
  /**
   * Keep a log of every input as it is applied, for `takeConsumedInputs`.
   *
   * Off unless asked for. The server asks: it relays the log to every other
   * simulation of the board and drains it as it goes. A client's boards never
   * drain it, so a client that kept it would grow one entry per input for the
   * whole round with nothing ever reading them.
   */
  readonly relayLog?: boolean;
}

export class SimDriver {
  /** Events not yet due, keyed by the frame they belong to. */
  private readonly pending = new Map<number, InputEvent[]>();
  /** Batches whose `queueAtTick` has not arrived yet, oldest first. */
  private scheduled: GarbageSchedule[] = [];
  /**
   * Curse windows this board has not finished living through yet — future
   * ones and ones currently active. Empty for the whole life of a driver
   * outside the `curses` variant.
   */
  private curseWindows: CurseWindow[] = [];
  private readonly checkpointQueue: HashCheckpoint[] = [];
  /** The relay log, or `null` when this driver was not asked to keep one. */
  private readonly consumedQueue: InputEvent[] | null;
  private readonly hashEveryNPieces: number;
  private readonly tickRule: TickRule | null;
  private readonly gravityPermille: number;

  constructor(
    public readonly state: GameState,
    options: SimDriverOptions,
  ) {
    this.hashEveryNPieces = Math.max(1, options.hashEveryNPieces);
    this.tickRule = options.tickRule ?? null;
    this.consumedQueue = options.relayLog === true ? [] : null;
    this.gravityPermille = options.gravityPermille ?? GRAVITY_PERMILLE;
    if (!Number.isSafeInteger(this.gravityPermille) || this.gravityPermille <= 0) {
      throw new RangeError(
        `SimDriver: gravityPermille must be a positive integer, got ${String(options.gravityPermille)}`,
      );
    }
  }

  /** The tick this simulation has reached. */
  get tick(): number {
    return this.state.tick;
  }

  get toppedOut(): boolean {
    return this.state.topOut;
  }

  /** Garbage queued against this board and not yet landed — the incoming meter's number. */
  get incomingLines(): number {
    let total = 0;
    for (const entry of this.state.pendingGarbage) total += entry.lines;
    return total;
  }

  /** Batches the server has announced but which have not entered the queue yet. */
  get scheduledGarbage(): readonly GarbageSchedule[] {
    return this.scheduled;
  }

  /**
   * Queue an input for the tick its frame names.
   *
   * An event for a tick already run is applied on the next tick instead of
   * being dropped: dropping it would diverge the two sides silently, whereas
   * applying it late diverges them *loudly*, at the next hash checkpoint,
   * which is exactly what the checkpoint is for. In the ordinary case this
   * never happens, because neither end advances past a frame it has been told
   * about.
   */
  enqueueInput(event: InputEvent): void {
    const frame = Math.max(event.frame, this.state.tick);
    const bucket = this.pending.get(frame);
    // The slot rides through untouched. It is only ever present on a relay the
    // server stamped, and the engine puts every event of a tick into ascending
    // slot order before applying any of them — so what the driver has to
    // preserve is *which player*, never *in what order they arrived*.
    const converted: InputEvent =
      event.slot === undefined
        ? { frame, action: event.action }
        : { frame, action: event.action, slot: event.slot };
    if (bucket === undefined) this.pending.set(frame, [converted]);
    else bucket.push(converted);
  }

  enqueueInputs(events: readonly InputEvent[]): void {
    for (const event of events) this.enqueueInput(event);
  }

  /**
   * Schedule a garbage batch. Both ends call this with the identical
   * `queueAtTick` and `readyAtTick` the server chose.
   *
   * A batch that is not three whole, non-negative numbers is refused: it would
   * put a fraction, or nothing at all, into the garbage queue the hash writes.
   */
  scheduleGarbage(batch: GarbageSchedule): void {
    if (!isGarbageSchedule(batch)) {
      throw new RangeError(`SimDriver: malformed garbage batch ${JSON.stringify(batch)}`);
    }
    this.scheduled.push(batch);
  }

  scheduleGarbageBatches(batches: readonly GarbageSchedule[]): void {
    for (const batch of batches) this.scheduleGarbage(batch);
  }

  /**
   * Announce a curse window. Both ends call this with the identical
   * `startTick`/`endTick` the server chose — the server because it just
   * decided them, a client because they rode a `sync`, a `resync` or a
   * `resume`.
   *
   * Unlike a `GarbageSchedule` there is nothing to "apply" at a moment: a
   * window is a fact a query answers, every tick, for as long as it is worth
   * answering. `pruneExpiredCurses` is the only thing that ever removes one,
   * and it removes it only once nothing could still be asking about it.
   *
   * A malformed window (`isCurseWindow`) is refused, for the same reason a
   * malformed batch is.
   */
  scheduleCurse(window: CurseWindow): void {
    if (!isCurseWindow(window)) {
      throw new RangeError(`SimDriver: malformed curse window ${JSON.stringify(window)}`);
    }
    this.curseWindows.push(window);
  }

  scheduleCurses(windows: readonly CurseWindow[] | undefined): void {
    for (const window of windows ?? []) this.scheduleCurse(window);
  }

  /**
   * Every curse window this board has not finished living through — active
   * now or starting later. What rides a resync, a resume or a checkpoint, so
   * a driver rebuilt mid-effect knows the remaining half of it without being
   * told twice.
   */
  get scheduledCurses(): readonly CurseWindow[] {
    return this.curseWindows;
  }

  /**
   * Checkpoints reached since the last call, in order, and clear them.
   *
   * A queue rather than a callback so the driver stays a plain object with no
   * inversion of control: the server drains it after `advanceTo`, the client
   * drains it after each tick of its own loop, and neither has to know when
   * the other does.
   */
  takeCheckpoints(): readonly HashCheckpoint[] {
    if (this.checkpointQueue.length === 0) return NO_CHECKPOINTS;
    const drained = this.checkpointQueue.slice();
    this.checkpointQueue.length = 0;
    return drained;
  }

  /**
   * The inputs actually applied since the last call, in order, and clear them.
   *
   * The server relays *these*, not the ones a client sent: an event for a
   * frame the server has not reached yet is a promise, and relaying a promise
   * would leave every observer holding inputs that a resync then invalidates.
   * Relaying only what has been consumed makes the relay stream exactly the
   * server's own history, which is what a mirror is supposed to reproduce.
   *
   * Always empty for a driver built without `relayLog`.
   */
  takeConsumedInputs(): readonly InputEvent[] {
    if (this.consumedQueue === null || this.consumedQueue.length === 0) return NO_INPUTS;
    const drained = this.consumedQueue.slice();
    this.consumedQueue.length = 0;
    return drained;
  }

  /**
   * Forget every input that has not been applied yet.
   *
   * Called on both sides of a resync. The client is about to rewind to the
   * server's snapshot and re-decide what to do from there, so anything either
   * side was still holding for a future frame belongs to a history that no
   * longer happened.
   */
  clearPendingInputs(): void {
    this.pending.clear();
  }

  /** Advance exactly one tick. This is the primitive; everything else loops it. */
  stepOnce(extraEvents: readonly InputEvent[] = NO_INPUTS): StepResult {
    this.enqueueInputs(extraEvents);
    this.insertDueGarbage();
    // A topped-out board is not stepped by the engine either (`step.ts`'s
    // `runTick` returns immediately), so nothing below may run against one:
    // `applyTickRule` skips it, and so do the gravity modifier and the
    // pruning of curse windows — none of it should touch a board nobody is
    // still stepping.
    applyTickRule(this.state, this.tickRule);
    if (!this.state.topOut) {
      this.pruneExpiredCurses();
      applyGravityModifier(
        this.state,
        gravityModifierAt(this.gravityPermille, this.curseWindows, this.state.tick),
      );
    }

    const tick = this.state.tick;
    const events = this.pending.get(tick) ?? [];
    this.pending.delete(tick);
    if (this.consumedQueue !== null) {
      for (const event of events) {
        this.consumedQueue.push(
          event.slot === undefined
            ? { frame: tick, action: event.action }
            : { frame: tick, action: event.action, slot: event.slot },
        );
      }
    }

    // MIRROR inverts `left`/`right` for the engine's own replay only — never
    // for what the relay log carries. A relay carries the event exactly as
    // it was received; every other simulation of this board runs the same
    // curse windows and inverts it again for itself, which is what keeps a
    // relayed event a single, unambiguous fact rather than something whose
    // meaning depends on which board it is being fed into.
    const simEvents =
      this.curseWindows.length === 0 || !curseActiveAt(this.curseWindows, 'mirror', tick)
        ? events
        : events.map((event) => ({ ...event, action: invertLeftRight(event.action) }));

    const before = this.state.piecesPlaced;
    const result = step(this.state, simEvents);

    if (this.state.piecesPlaced !== before) {
      // The hash is taken once, after the whole tick — inputs, gravity, the
      // lock, the clear and any garbage release. Two pieces can lock in one
      // tick in a shared well, and one hash still covers the tick: the loop
      // asks whether any of the placements crossed a multiple of N, so a
      // checkpoint is never skipped because the count jumped past it.
      let due = false;
      for (let placed = before + 1; placed <= this.state.piecesPlaced; placed++) {
        if (placed % this.hashEveryNPieces === 0) due = true;
      }
      if (due) {
        this.checkpointQueue.push({
          piecesPlaced: this.state.piecesPlaced,
          hash: hashState(this.state),
        });
      }
    }

    return result;
  }

  /**
   * Advance until `targetTick`, or not at all if it is already past.
   *
   * A topped-out board stops advancing: the round is over for that seat and
   * running further ticks would only move a counter nobody reads.
   */
  advanceTo(targetTick: number): DriveResult {
    const startTick = this.state.tick;
    const locks: LockResult[] = [];
    let garbageApplied = 0;

    while (this.state.tick < targetTick && !this.state.topOut) {
      const result = this.stepOnce();
      garbageApplied += result.garbageApplied;
      for (const lock of result.locks) locks.push(lock);
    }

    return {
      ticksAdvanced: this.state.tick - startTick,
      locks,
      garbageApplied,
      toppedOut: this.state.topOut,
    };
  }

  /** A deep copy of the live state, for a snapshot or a comparison. */
  snapshotState(): GameState {
    return cloneGameState(this.state);
  }

  /**
   * Move every batch whose `queueAtTick` has arrived into the engine's own
   * pending-garbage queue, oldest first.
   *
   * Order is not cosmetic: `cancelPendingGarbage` spends an outgoing attack on
   * the *oldest* batch first, and the queue's contents are part of the state
   * hash. Two sides that inserted the same batches in a different order would
   * cancel differently and hash differently. Sorting by `queueAtTick` and then
   * by `readyAtTick` makes the order a function of the server's own numbers
   * rather than of arrival time — which is what "the server owns the
   * cancellation order" (BASELINE invariant 6) means in practice.
   */
  private insertDueGarbage(): void {
    if (this.scheduled.length === 0) return;
    const tick = this.state.tick;

    const due = this.scheduled.filter((batch) => batch.queueAtTick <= tick);
    if (due.length === 0) return;
    this.scheduled = this.scheduled.filter((batch) => batch.queueAtTick > tick);

    due.sort((a, b) =>
      a.queueAtTick !== b.queueAtTick
        ? a.queueAtTick - b.queueAtTick
        : a.readyAtTick - b.readyAtTick,
    );

    for (const batch of due) {
      queueGarbage(this.state, batch.lines, batch.readyAtTick - tick);
    }
  }

  /**
   * Drop curse windows nothing could still be asking about.
   *
   * A window whose `endTick` has passed can no longer answer `curseActiveAt`
   * with `true` for any tick this driver will ever be asked about again —
   * ticks only go forward — so keeping it costs memory for nothing. This is
   * hygiene, not a state change: removing an already-inert window changes no
   * query's answer, on this driver or any other.
   */
  private pruneExpiredCurses(): void {
    if (this.curseWindows.length === 0) return;
    const tick = this.state.tick;
    this.curseWindows = this.curseWindows.filter((window) => window.endTick > tick);
  }
}
